/**
 * @file PaperworkHistoryModal.js
 * @version 1.2.1
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-09-30
 * @modified 2026-10-30
 * @description Every send of one kind of paperwork for a delivery day — who
 *              sent it, when, to whom, and whether it was delivered.
 *
 *              Opened by clicking a board's "sent" pill once it is expanded.
 *              Delivery status is SES's, from the tracked email behind each
 *              send (GET /api/transport/planning/send-history/:date).
 *
 *              1.1.0 (2026-10-14): a driver pack copied to the office (Cc)
 *              says so beside its address.
 *
 *              1.2.0 (2026-10-20): a driver pack says which top sheet it went
 *              with — "detailed day sheet" or "one row per order" (sends from
 *              before the per-driver choice say nothing).
 *
 *              1.2.1 (2026-10-30): the status reads by the shared rule
 *              (utils/emailDelivery) — "complained" and "rejected" read as
 *              "Marked as spam" and "Rejected", as everywhere else.
 */

import React, { useEffect, useState } from 'react';
import Modal from './Modal';
import LoadingSpinner from './LoadingSpinner';
import { API_URL } from '../config';
import './PaperworkHistoryModal.css';
import SentEmailModal from './SentEmailModal';
import { deliveryStatus } from '../utils/emailDelivery';


// Which top sheet a driver pack went with (routes/transport.js generate-paperwork).
const LAYOUT = {
  detailed: { label: 'detailed day sheet', title: 'Lorry day sheet: Collect, D1, D2… with compartments' },
  simple: { label: 'one row per order', title: 'One row per order, ForFarmers orders with their compartments' },
  old_rules: { label: 'one row per order', title: 'A day planned before lorry day planning — printed as it was planned' },
  plan_unavailable: { label: 'one row per order', title: 'The lorry day plans could not be read — printed one row per order' }
};

const when = (d) => new Date(d).toLocaleString('en-GB', { weekday: 'short', day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });

function PaperworkHistoryModal({ isOpen, onClose, date, kind, title }) {
  const [history, setHistory] = useState(null);
  const [error, setError] = useState(null);
  const [viewEmailId, setViewEmailId] = useState(null);

  useEffect(() => {
    if (!isOpen || !date) return undefined;
    let live = true;
    setHistory(null);
    setError(null);
    fetch(`${API_URL}/api/transport/planning/send-history/${date}?kind=${kind}`, { credentials: 'include' })
      .then(r => r.json().then(d => ({ ok: r.ok, d })))
      .then(({ ok, d }) => {
        if (!live) return;
        if (!ok) throw new Error(d.error || 'Failed to load history');
        setHistory(d.history || []);
      })
      .catch(e => live && setError(e.message));
    return () => { live = false; };
  }, [isOpen, date, kind]);

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={title} size="lg" className="paperwork-history">
      {error && <p className="pwh-error">{error}</p>}
      {!error && !history && <LoadingSpinner />}
      {history && history.length === 0 && <p className="pwh-empty">Nothing has been sent for this day.</p>}
      {history && history.map(entry => (
        <section key={entry.id} className="pwh-send">
          <header className="pwh-send-head">
            <span className="material-symbols-rounded">send</span>
            <strong>{when(entry.sent_at)}</strong>
            {entry.sent_by_name && <span className="pwh-by">by {entry.sent_by_name}</span>}
            <span className="pwh-count">{entry.recipients.length} email{entry.recipients.length === 1 ? '' : 's'}</span>
          </header>
          <table className="pwh-table">
            <thead>
              <tr><th>Recipient</th><th>Sent to</th><th className="num">Orders</th><th>Status</th><th className="pwh-view-col"></th></tr>
            </thead>
            <tbody>
              {entry.recipients.map((r, i) => {
                const st = deliveryStatus(r.delivery || 'unknown');
                return (
                  <tr key={i}>
                    <td>{r.name}</td>
                    <td>
                      {r.emails.length ? r.emails.join(', ') : '—'}
                      {r.toOffice && <span className="pwh-office">office</span>}
                      {r.cc && <span className="pwh-office" title={`Copy to ${r.cc}`}>copy to office</span>}
                      {LAYOUT[r.layout] && <span className="pwh-office pwh-layout" title={LAYOUT[r.layout].title}>{LAYOUT[r.layout].label}</span>}
                    </td>
                    <td className="num">{r.orderCount}</td>
                    <td>
                      <span className={`pwh-status ${st.tone}`} title={r.error || (r.deliveryAt ? `Last update ${when(r.deliveryAt)}` : '')}>
                        <span className="material-symbols-rounded">{st.icon}</span>{st.label}
                      </span>
                      {r.error && <div className="pwh-err-text">{r.error}</div>}
                    </td>
                    <td className="pwh-view-col">
                      <button type="button" className={`btn-icon${r.emailId ? '' : ' btn-disabled'}`} disabled={!r.emailId}
                        title={r.emailId ? 'View the email as it was sent' : 'No tracked email for this send'}
                        onClick={() => setViewEmailId(r.emailId)}>
                        <span className="material-symbols-rounded">mail</span>
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </section>
      ))}
      <SentEmailModal isOpen={viewEmailId != null} onClose={() => setViewEmailId(null)} emailId={viewEmailId} />
    </Modal>
  );
}

export default PaperworkHistoryModal;
